import {
  WorkflowTracker,
  WorkflowTrackerKind,
  WorkflowTrackerStatus,
} from '@/types/schema';

type ProcessingIndicatorWorkflowTracker = Pick<
  WorkflowTracker,
  'householdID' | 'kind' | 'status'
>;

const VALIDATION_PROCESSING_KINDS = [
  WorkflowTrackerKind.ValidationQuestions,
  WorkflowTrackerKind.DocumentExtraction,
];

/**
 * Shows the global processing indicator unless the validation callout already
 * represents all active processing for its household.
 *
 * @param validationCalloutHouseholdId Household represented by the visible
 * validation callout. It is undefined when there is no callout.
 */
export function shouldShowProcessingIndicator(
  trackers: ProcessingIndicatorWorkflowTracker[],
  validationCalloutHouseholdId?: string
): boolean {
  const activeTrackers = trackers.filter(
    (tracker) => tracker.status === WorkflowTrackerStatus.Started
  );

  // There is no processing to indicate.
  if (activeTrackers.length === 0) return false;

  // Without a callout, the indicator is the only processing status in the app bar.
  if (!validationCalloutHouseholdId) return true;

  const hasActiveValidationQuestions = activeTrackers.some(
    (tracker) =>
      tracker.householdID === validationCalloutHouseholdId &&
      tracker.kind === WorkflowTrackerKind.ValidationQuestions
  );

  // The callout cannot represent active processing for a different household.
  if (!hasActiveValidationQuestions) return true;

  const onlyValidationProcessingIsActive = activeTrackers.every(
    (tracker) =>
      tracker.householdID === validationCalloutHouseholdId &&
      VALIDATION_PROCESSING_KINDS.includes(tracker.kind)
  );

  // Keep the indicator when any unrelated workflow still needs global status.
  return !onlyValidationProcessingIsActive;
}
